import React, { useState, useEffect, useRef } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiService from "../actions/apiService";

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const res = await apiService.get("/notifications/unread-count");
      if (res.success) {
        setUnreadCount(res.data?.count || 0);
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await apiService.get("/notifications", {
        page: 1,
        limit: 10,
      });
      if (res.success) {
        setNotifications(res.data?.notifications || []);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await apiService.patch(`/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId
            ? { ...notif, is_read: true, read_at: new Date() }
            : notif
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await apiService.patch("/notifications/read-all");
      setNotifications((prev) =>
        prev.map((notif) => ({
          ...notif,
          is_read: true,
          read_at: new Date(),
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  // Handle generic notification click - mark as read
  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
  };

  // Special handler: buyer's accepted offer → navigate to payment page
  const handleAcceptedOfferNotification = async (notification) => {
    try {
      // Mark as read first
      if (!notification.is_read) {
        await markAsRead(notification.id);
      }

      const requestId = notification.related_entity_id;
      if (!requestId) return;

      // Fetch accepted requests for the buyer and find this one
      const reqRes = await apiService.get("/requests", {
        role: "buyer",
        status: "accepted",
        page: 1,
        limit: 100,
      });

      if (!reqRes?.success) return;

      const list =
        reqRes.data?.requests ||
        reqRes.data?.data?.requests ||
        reqRes.data ||
        [];
      const request = Array.isArray(list)
        ? list.find((r) => r.id === requestId)
        : null;

      const itemId = request?.item?.id;
      if (!itemId) return;

      // Navigate buyer to payment instructions page for this item
      // Transaction will be created when user clicks "I made the transfer" button
      navigate(`/buyer/payment-initiate/${itemId}`);
    } catch (error) {
      console.error("Failed to handle accepted offer notification:", error);
    }
  };

  // Fetch data on mount and when dropdown opens
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleBellClick = () => {
    if (!isOpen) {
      // When opening: clear notifications, set loading, then open dropdown
      setNotifications([]);
      setLoading(true);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleBellClick}
        className="relative inline-flex h-[42px] w-[42px] items-center justify-center rounded-[8px] border border-[#1E1E1E0D] hover:bg-slate-50 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={20} className="text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[#E84B39] text-white text-[10px] font-semibold flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-[-50px] mt-2 w-72 sm:w-80 md:w-96 bg-white rounded-lg  border border-gray-100 z-50 max-h-[400px] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-rubik font-semibold text-gray-900">
              Notifications
            </h3>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-[#1E1E1E] opacity-50">
                <div className="flex font-medium text-sm flex-col items-center justify-center">
                  <p>Loading notifications...</p>
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-[#1E1E1E] opacity-50">
                <Bell
                  size={32}
                  className="mx-auto mb-2 text-[#1E1E1E] opacity-50"
                />
                <p>No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => {
                  const isAcceptedOffer =
                    notification?.type === "purchase_request_accepted";

                  return (
                    <div
                      key={notification.id}
                      className={`w-full p-4 transition-colors ${
                        !notification.is_read ? "bg-blue-50" : "bg-white"
                      }`}
                    >
                      <div
                        className="flex items-start gap-3 cursor-pointer"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div
                          className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                            !notification.is_read
                              ? "bg-esblue"
                              : "bg-transparent"
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1E1E1E]">
                            {notification.title}
                          </p>
                          <p className="text-xs text-[#1E1E1E] opacity-75 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-[#1E1E1E] opacity-50 mt-1">
                            {formatTime(notification.created_at)}
                          </p>
                        </div>
                      </div>
                      {isAcceptedOffer && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcceptedOfferNotification(notification);
                          }}
                          className="mt-3 w-full bg-[#B9FFCC] hover:bg-[#A8F5B8] text-[#16653F] rounded-[8px] py-2.5 px-4 flex items-center justify-center gap-2 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="13"
                            viewBox="0 0 18 13"
                            fill="none"
                            className="flex-shrink-0"
                          >
                            <path
                              d="M17 1L6 12L1 7"
                              stroke="#16653F"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span className="font-semibold text-[14px]">
                            Proceed to payment
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {unreadCount > 0 && notifications.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={markAllAsRead}
                className="w-full py-2 px-4 bg-blue-gradient text-white rounded-lg font-medium text-sm"
              >
                Mark all as read
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
